import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Mail, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import logo from '../../logo.jpg';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { login, isConfigured } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!isConfigured) {
        setError('Supabase connection credentials are missing. Please add your Supabase URL and Anon Key to frontend/.env to enable live admin authentication.');
        return;
      }

      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message || 'Authentication failed. Please verify your admin credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-950 flex items-center justify-center p-4 sm:p-6 relative overflow-hidden"
      style={{ minHeight: '100vh', backgroundColor: '#020617', color: '#f8fafc' }}
    >
      {/* Aurora Background Effect */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#9BB7A3]/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-slate-800/40 rounded-full blur-[140px] pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Brand Card */}
        <div className="bg-slate-900/90 border border-slate-800/90 rounded-3xl p-8 sm:p-10 shadow-2xl backdrop-blur-xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-white/10 p-2 mx-auto mb-4 border border-white/10 flex items-center justify-center shadow-lg">
              <img src={logo} alt="VJ Dsign Studio" className="w-12 h-12 object-contain" />
            </div>
            <h2 className="text-2xl font-black text-white tracking-wide">VJ Dsign Studio</h2>
            <p className="text-xs font-bold text-[#9BB7A3] uppercase tracking-widest mt-1">
              Admin Portal
            </p>
          </div>

          {!isConfigured && (
            <div className="mb-6 p-4 rounded-2xl bg-amber-950/40 border border-amber-800/40 text-amber-300 text-xs leading-relaxed flex items-start gap-3">
              <AlertCircle size={18} className="shrink-0 mt-0.5 text-amber-400" />
              <div>
                <p className="font-bold mb-1">Supabase Configuration Required</p>
                <p className="text-amber-300/80 mb-2">
                  No mock or fake database is used. Provide your Supabase project credentials in <code className="bg-amber-900/50 px-1 py-0.5 rounded text-amber-200 font-mono">frontend/.env</code>:
                </p>
                <div className="bg-black/40 p-2.5 rounded-xl font-mono text-[11px] text-slate-300 select-all space-y-0.5 border border-amber-900/30">
                  <div>REACT_APP_SUPABASE_URL=...</div>
                  <div>REACT_APP_SUPABASE_ANON_KEY=...</div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-rose-950/50 border border-rose-800/50 text-rose-300 text-xs font-medium flex items-center gap-2.5">
              <AlertCircle size={16} className="shrink-0 text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Admin Email
              </label>
              <div className="relative">
                <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  required={isConfigured}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@vjdsignstudio.com"
                  className="w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3] focus:ring-1 focus:ring-[#9BB7A3] transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required={isConfigured}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full pl-11 pr-11 py-3 bg-slate-950 border border-slate-800 rounded-xl text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3] focus:ring-1 focus:ring-[#9BB7A3] transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-6 bg-[#9BB7A3] hover:bg-[#8aa892] text-slate-950 font-bold text-sm uppercase tracking-wider rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#9BB7A3]/20 active:scale-95 disabled:opacity-50 mt-6"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Sign In To Dashboard</span>
                  <ArrowRight size={17} />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-800/80 text-center">
            <a
              href="/"
              className="text-xs font-semibold text-slate-400 hover:text-slate-200 transition inline-flex items-center gap-1.5"
            >
              ← Return to Public Website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
